import { useAuth } from '../stores/AuthContext';

/**
 * Custom hook for subscription state and feature access checks.
 * Wraps AuthContext user data with convenience methods.
 */
export function useSubscription() {
  const { user, refreshSubscription } = useAuth();

  const subscription = user?.subscription || { plan_id: 'free', status: 'active' };
  const planFeatures = user?.planFeatures || {};
  const usage = user?.usage || {};
  const planId = subscription.plan_id || 'free';

  return {
    // Plan identity
    planId,
    isFreeTier: planId === 'free',
    isBasicOrAbove: ['basic', 'pro', 'elite'].includes(planId),
    isProOrAbove: ['pro', 'elite'].includes(planId),
    isElite: planId === 'elite',

    // Subscription status
    status: subscription.status,
    cancelAtPeriodEnd: subscription.cancel_at_period_end,
    periodEnd: subscription.current_period_end,
    billingInterval: subscription.billing_interval,

    // Feature access checks
    hasFeature: (featureKey) => {
      const f = planFeatures[featureKey];
      if (!f) return false;
      if (f.limit_type === 'boolean') return f.limit_value === 1;
      if (f.limit_type === 'unlimited') return true;
      // Rate-limited features are "available" (just may be at limit)
      return true;
    },

    // Get feature limit info
    getLimit: (featureKey) => {
      return planFeatures[featureKey] || null;
    },

    // Get usage data for a feature
    getUsage: (featureKey) => {
      return usage[featureKey] || { used: 0, limit: null };
    },

    // Check if a rate-limited feature is at its limit
    isAtLimit: (featureKey) => {
      const f = planFeatures[featureKey];
      if (!f) return true;
      if (f.limit_type === 'unlimited' || f.limit_type === 'boolean') return false;
      const u = usage[featureKey];
      if (!u) return false;
      return u.limit !== null && u.used >= u.limit;
    },

    // Raw data
    subscription,
    planFeatures,
    usage,

    // Refresh
    refreshSubscription,
  };
}
